import 'bootstrap/dist/css/bootstrap.min.css';
import { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { InfoCircle, Printer } from 'react-bootstrap-icons';
import Button from '../../components/Button';
import { ExtractTable } from '../../components/ExtractTable';
import { PrintingVersion } from '../../components/PrintingVersion';
import { useUserContext } from '../../context/UserContext';
import { BoxCard, InfoTitle, PageTitleContainer, Title } from './styles';
import {
  ConsolidatedPositionProp,
  getConsolidatedPosition,
  naoContempladasProp,
} from '../../services/consolidatedPosition';
import { formatMoney } from '../../constants/functions';
import { useNavigate } from 'react-router-dom';

const calcTotalByCollum = (data: any[], key: string): number => {
  return data.reduce((acc, item) => {
    const value = Number(String(item[key]).replace(/\./g, '').replace(',', '.'));
    return acc + (isNaN(value) ? 0 : value);
  }, 0);
};

const removeDuplicateCotas = (cotas: any[]) => {
  const seen = new Map<string, any>();
  cotas.forEach((cota) => {
    const key = `${cota.grupo}/${cota.cota}`;
    if (!seen.has(key)) {
      seen.set(key, cota);
    }
  });
  return Array.from(seen.values());
};

const ConsolidatedPosition = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const { group, quota, userData, loginResponseData, setQuota, setGroup } = useUserContext();
  const [consolidatedPosition, setConsolidatedPosition] = useState<ConsolidatedPositionProp>();
  const [differentDueDates, setDifferentDueDates] = useState<naoContempladasProp[][]>([]);
  const navigate = useNavigate();

  const handlePrint = useReactToPrint({
    content: () => ref?.current,
    onAfterPrint: () => setIsPrinting(false),
  });

  useEffect(() => {
    if (!loginResponseData) return;

    const fetchData = async () => {
      try {
        const info = await getConsolidatedPosition();
        const groupBy = (arr: any[], property: string) => {
          return arr.reduce((acc, cur) => {
            acc[cur[property]] = [...(acc[cur[property]] || []), cur];
            return acc;
          }, {});
        };
        setDifferentDueDates(groupBy(info!.naoContempladas, 'data'));
        setConsolidatedPosition(info as ConsolidatedPositionProp);
      } catch (e) {
        console.error(e);
      }
    };

    fetchData();
  }, [loginResponseData]);

  const allCotas = [
    ...(consolidatedPosition?.naoContempladas || []),
    ...(consolidatedPosition?.contempladas || []),
    ...(consolidatedPosition?.canceladas || []),
  ];
  const uniqueCotas = removeDuplicateCotas(allCotas);

  const totalCredito = calcTotalByCollum(uniqueCotas, 'credito');
  const totalValorParcela = calcTotalByCollum(uniqueCotas, 'valorParcela');
  const totalSaldoDevedor = calcTotalByCollum(uniqueCotas, 'saldoDevedor');
  const totalSaldoPago = calcTotalByCollum(uniqueCotas, 'saldoPago');

  const pageContent = () => (
    <>
      <PageTitleContainer>
        <div>
          <Title>Posição Consolidada</Title>
          <InfoTitle>
            <InfoCircle size={22} />
            <span>Clique em uma cota para emitir o boleto</span>
          </InfoTitle>
        </div>
        <Button onClick={handlePrint} hideOnPrint={true}>
          <Printer /> Imprimir
        </Button>
      </PageTitleContainer>

      {Object.keys(differentDueDates).map((dueDate: any) => (
        <BoxCard key={dueDate}>
          <ExtractTable
            title={`Cota(s) não contemplada(s) com vencimento em ${differentDueDates[dueDate][0].data}`}
            rowsGroup={[
              'Grupo/Cota',
              'Valor crédito',
              'Valor parcela',
              'Saldo devedor',
              '% Devedor',
              'Total pago',
              '% Pago',
            ]}
            data={differentDueDates[dueDate].map((naoContemplada) => {
              const { cota, credito, grupo, percentualDevedor, percentualPago, saldoDevedor, saldoPago, valorParcela } =
                naoContemplada;

              return [
                grupo + '/' + cota,
                formatMoney(credito),
                formatMoney(valorParcela),
                formatMoney(saldoDevedor),
                percentualDevedor,
                formatMoney(saldoPago),
                percentualPago,
              ];
            })}
            totalValues={
              differentDueDates[dueDate].length > 1
                ? [
                    'Total: ' + differentDueDates[dueDate].length,
                    formatMoney(calcTotalByCollum(differentDueDates[dueDate], 'credito')),
                    formatMoney(calcTotalByCollum(differentDueDates[dueDate], 'valorParcela')),
                    formatMoney(calcTotalByCollum(differentDueDates[dueDate], 'saldoDevedor')),
                    '',
                    formatMoney(calcTotalByCollum(differentDueDates[dueDate], 'saldoPago')),
                    '',
                  ]
                : []
            }
            withLink={(e) => {
              setQuota(differentDueDates[dueDate][e].cota);
              setGroup(differentDueDates[dueDate][e].grupo);
              setTimeout(() => navigate('/emissao-de-boleto'), 100);
            }}
          />
        </BoxCard>
      ))}

      <BoxCard>
        <ExtractTable
          title="Cotas"
          rowsGroup={['Cotas', 'Valor crédito', 'Valor parcela', 'Saldo devedor', 'Total pago']}
          data={uniqueCotas.map((cota) => [
            `${cota.grupo}/${cota.cota}`,
            formatMoney(cota.credito),
            formatMoney(cota.valorParcela),
            formatMoney(cota.saldoDevedor),
            formatMoney(cota.saldoPago),
          ])}
          totalValues={[
            'Geral: ' + uniqueCotas.length,
            formatMoney(totalCredito),
            formatMoney(totalValorParcela),
            formatMoney(totalSaldoDevedor),
            formatMoney(totalSaldoPago),
          ]}
        />
      </BoxCard>
    </>
  );

  return (
    <>
      {consolidatedPosition && (
        <>
          <div ref={ref}>
            <PrintingVersion>{pageContent()}</PrintingVersion>
          </div>
          <div>{pageContent()}</div>
        </>
      )}
    </>
  );
};

export default ConsolidatedPosition;
